import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as CatalogsActions from './catalogs.actions';
import { CatalogsStoreService } from './catalogs.service';
import { ArkikAlertService } from 'src/app/shared/components/arkik-alert/arkik-alert.service';
import { TranslationService } from 'src/app/core/services/common/translation.service';
import { map, switchMap, catchError, mergeMap, take, timeout, takeUntil } from 'rxjs/operators';
import { Observable, defer, from, of, merge } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectCatalogs } from './catalogs.selectors';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { CatalogsInterface } from './catalogs.models';
import { environment } from 'src/environments/environment';
import { CATALOG_OPERATION_TIMEOUT_MS, reportCatalogError } from '@core/services/common/catalog-operation';
import { loadLogin, logout, resetLogin } from '../login/login.actions';

const hasCompleteCatalogsData = (c: CatalogsInterface | null | undefined): boolean =>
  !!c && [c.Countries, c.OrderStatuses, c.OrderItemStatuses, c.PlantOperativeModes].every((list) => list?.length > 0);

@Injectable()
export class CatalogsEffects {
  constructor(
    private actions$: Actions,
    private catalogsService: CatalogsStoreService,
    private alert: ArkikAlertService,
    private translation: TranslationService,
    private store: Store,
    private idb: ManageIdbService,
  ) {}

  loadCatalogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogsActions.loadCatalogs),
      mergeMap((request) =>
        defer(() => this.idb.prepareCatalogAccess(this.idb.catalogEpoch)).pipe(
          switchMap(() => this.store.select(selectCatalogs)),
          take(1),
          switchMap((catalogs) => this.load(catalogs, this.idb.catalogEpoch)),
          timeout(CATALOG_OPERATION_TIMEOUT_MS),
          map((catalogs) => CatalogsActions.loadCatalogsSuccess({ ...request, catalogs })),
          catchError((error: unknown) =>
            of(
              CatalogsActions.loadCatalogsFailure({
                ...request,
                error: reportCatalogError(
                  this.alert,
                  'catalogs',
                  request,
                  error,
                  this.translation.pt('views.globals.an-error-occurred'),
                  this.translation.pt('views.globals.reload'),
                ),
              }),
            ),
          ),
          takeUntil(merge(this.idb.catalogCancellation$, this.actions$.pipe(ofType(loadLogin, logout, resetLogin)))),
        ),
      ),
    ),
  );

  refreshCatalogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogsActions.refreshCatalogs),
      map(() => CatalogsActions.resetCatalogs()),
    ),
  );

  private load(catalogs: CatalogsInterface | null, epoch: number): Observable<CatalogsInterface> {
    if (hasCompleteCatalogsData(catalogs)) {
      return this.restoreIdbFromStore(catalogs, epoch).pipe(
        switchMap(() =>
          environment.isLocalWeb ? of({ areas: [], regions: [] }) : this.catalogsService.getAreasRegions(),
        ),
        switchMap(({ areas, regions }) =>
          from(Promise.all([this.idb.saveAreas(areas, epoch), this.idb.saveRegions(regions, epoch)])),
        ),
        map(() => catalogs),
      );
    }
    return defer(() => this.idb.selectCatalogsSnapshot()).pipe(
      switchMap((cached) => {
        if (cached) {
          return environment.isLocalWeb
            ? from(Promise.all([this.idb.saveAreas([], epoch), this.idb.saveRegions([], epoch)])).pipe(
                map(() => cached),
              )
            : of(cached);
        }
        const response$ = environment.isLocalWeb
          ? this.catalogsService.getCatalogs().pipe(map((response) => ({ catalogs: response, areas: [], regions: [] })))
          : this.catalogsService.getCatalogsAreasRegions();
        return response$.pipe(
          switchMap(({ catalogs: response, areas, regions }) =>
            from(
              Promise.all([
                this.idb.saveCatalogs(response, epoch),
                this.idb.saveAreas(areas, epoch),
                this.idb.saveRegions(regions, epoch),
              ]),
            ).pipe(map(() => response)),
          ),
        );
      }),
    );
  }

  private restoreIdbFromStore(catalogs: CatalogsInterface, epoch: number): Observable<void> {
    return defer(() => this.idb.selectCatalogsSnapshot()).pipe(
      switchMap((snapshot) => (snapshot ? of(undefined) : from(this.idb.saveCatalogs(catalogs, epoch)))),
    );
  }
}
